import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useClinicSubscription } from "../features/subscriptions/hooks/useClinicSubscription"
import { useClinicPlanSnapshot } from "../features/subscriptions/hooks/useClinicPlanSnapshot"
import SubscriptionFeatureAccess from "../features/subscriptions/components/SubscriptionFeatureAccess"
import { apiFetch } from "../utils/apiFetch"
import { useActiveClinic } from "../context/ClinicContext"

const API_BASE = (() => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1"

    return isLocalHost ? (["http://localhost", "3000/api"].join(":")) : "/api"
  }

  return "/api"
})()

const EMPTY_AGING = { buckets: [], rows: [] }
const EMPTY_CREDIT_BALANCE = {
  totalCreditBalance: 0,
  patientsWithCredit: 0,
  rows: []
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function formatDateOnly(value) {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("es-MX")
}

function getTodayDateInput() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}


function getBillingInitialRange(queryParams) {
  const routeDate = queryParams.get("date") || queryParams.get("day") || ""
  const routeDateFrom = queryParams.get("dateFrom") || routeDate || getTodayDateInput()
  const routeDateTo = queryParams.get("dateTo") || routeDate || routeDateFrom || getTodayDateInput()

  return {
    dateFrom: routeDateFrom,
    dateTo: routeDateTo
  }
}

function toCsvValue(value) {
  const text = value == null ? "" : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.map(toCsvValue).join(","),
    ...rows.map((row) => row.map(toCsvValue).join(","))
  ].join("\n")

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
}

function normalizeFeatureErrorPayload(rawError) {
  if (!rawError || typeof rawError !== "object") return null

  const payload =
    rawError?.code === "FEATURE_NOT_AVAILABLE"
      ? rawError
      : rawError?.response?.code === "FEATURE_NOT_AVAILABLE"
        ? rawError.response
        : null

  if (!payload) return null

  return {
    code: "FEATURE_NOT_AVAILABLE",
    message:
      payload?.message ||
      "Tu plan actual no incluye esta funcionalidad.",
    missingFeatures: Array.isArray(payload?.missingFeatures)
      ? payload.missingFeatures
      : [],
    recommendedPlans: Array.isArray(payload?.recommendedPlans)
      ? payload.recommendedPlans
      : [],
    currentPlan: payload?.currentPlan || null,
    clinicId: payload?.clinicId || null
  }
}

async function safeFetchResult(url, fallback) {
  try {
    const res = await apiFetch(url)
    const data = await res.json().catch(() => null)

    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        data: fallback,
        error: normalizeFeatureErrorPayload(data) || data || null
      }
    }

    return {
      ok: true,
      status: res.status,
      data: data ?? fallback,
      error: null
    }
  } catch (error) {
    return {
      ok: false,
      status: 0,
      data: fallback,
      error: error || null
    }
  }
}

function extractRecommendedPlan(payload, feature) {
  if (!Array.isArray(payload?.recommendedPlans)) return null
  return payload.recommendedPlans.find((item) => item?.feature === feature)?.recommendedPlan || null
}

function getDebtPriority(totalPending) {
  const value = Number(totalPending || 0)
  if (value >= 10000) return "CRITICAL"
  if (value >= 5000) return "HIGH"
  if (value >= 2000) return "MEDIUM"
  return "LOW"
}

function getCreditPriority(totalCredit) {
  const value = Number(totalCredit || 0)
  if (value >= 5000) return "HIGH"
  if (value >= 2000) return "MEDIUM"
  return "LOW"
}

function getBucketPriority(bucket) {
  if (bucket === "91+") return "CRITICAL"
  if (bucket === "61-90") return "HIGH"
  if (bucket === "31-60") return "MEDIUM"
  return "LOW"
}

function getBucketLabel(bucket, index = 0) {
  const value = String(bucket || "").trim()
  if (value) return value

  const fallback = ["0-30", "31-60", "61-90", "91+"]
  return fallback[index] || "0-30"
}

export default function BillingPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const { activeClinicId } = useActiveClinic()

  const clinicIdFromUrl = queryParams.get("clinicId") || ""
  const clinicIdFromStorage = localStorage.getItem("odontoflow_clinic_id") || ""
  const activeClinic =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : ""
  const isAllClinicsContext = activeClinicId === "ALL"

  const clinicId =
    activeClinic ||
    (!activeClinicId && clinicIdFromUrl && clinicIdFromUrl !== "ALL"
      ? clinicIdFromUrl
      : "") ||
    (!activeClinicId && clinicIdFromStorage && clinicIdFromStorage !== "ALL"
      ? clinicIdFromStorage
      : "")

  const { subscription } = useClinicSubscription(clinicId)
  const { data: planSnapshot } = useClinicPlanSnapshot(clinicId)

  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const plan = planSnapshot?.plan || subscription?.plan || null
  const currentPlanName = plan?.name || "Tu plan actual"

  const canBillingAdvancedByPlan = plan
    ? Boolean(plan.allowAdvancedReports || plan.allowExecutiveDashboard)
    : true

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isMobile = viewportWidth < 760

  const initialRange = useMemo(
    () => getBillingInitialRange(queryParams),
    // Solo se usa para inicializar estado; los cambios de URL se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom)
  const [dateTo, setDateTo] = useState(initialRange.dateTo)

  useEffect(() => {
    const routeDate = queryParams.get("date") || queryParams.get("day") || ""
    const routeDateFrom = queryParams.get("dateFrom") || routeDate || ""
    const routeDateTo = queryParams.get("dateTo") || routeDate || routeDateFrom || ""

    if (!routeDateFrom && !routeDateTo) return

    if (routeDateFrom) setDateFrom(routeDateFrom)
    if (routeDateTo) setDateTo(routeDateTo)
  }, [queryParams])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [summary, setSummary] = useState(null)
  const [topDebtors, setTopDebtors] = useState([])
  const [aging, setAging] = useState(EMPTY_AGING)
  const [creditBalance, setCreditBalance] = useState(EMPTY_CREDIT_BALANCE)
  const [billingFeatureLock, setBillingFeatureLock] = useState(null)

  const [search, setSearch] = useState("")
  const [workTab, setWorkTab] = useState("DEBTORS")

  const effectiveCanBillingAdvanced =
    canBillingAdvancedByPlan && !billingFeatureLock

  const resetBillingData = useCallback(() => {
    setSummary(null)
    setTopDebtors([])
    setAging(EMPTY_AGING)
    setCreditBalance(EMPTY_CREDIT_BALANCE)
  }, [])

  const openUpgrade = useCallback(
    (featureKey = "billingAdvanced") => {
      navigate(`/upgrade-plan?feature=${encodeURIComponent(featureKey)}`)
    },
    [navigate]
  )

  const loadData = useCallback(async () => {
    if (!clinicId) {
      setError(
        isAllClinicsContext
          ? "Cobros trabaja por clínica. Selecciona una clínica específica para cargar cartera y cobranza."
          : "No hay una clínica válida seleccionada."
      )
      setBillingFeatureLock(null)
      resetBillingData()
      return
    }

    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError("La fecha inicial no puede ser mayor que la fecha final.")
      return
    }

    if (plan && !canBillingAdvancedByPlan) {
      setError("")
      setBillingFeatureLock({
        message:
          "Tu plan actual no incluye cobranza avanzada, antigüedad ampliada, saldo a favor y herramientas de exportación.",
        currentPlan: { name: currentPlanName },
        recommendedPlan: null
      })
      resetBillingData()
      return
    }

    try {
      setLoading(true)
      setError("")

      const params = new URLSearchParams()
      params.set("clinicId", clinicId)
      if (dateFrom) params.set("dateFrom", dateFrom)
      if (dateTo) params.set("dateTo", dateTo)

      const result = await safeFetchResult(
        `${API_BASE}/finance/executive-bundle?${params.toString()}`,
        null
      )

      if (!result.ok) {
        const featurePayload = normalizeFeatureErrorPayload(result.error)

        if (featurePayload) {
          setBillingFeatureLock({
            message:
              featurePayload.message ||
              "Tu plan actual no incluye esta funcionalidad.",
            currentPlan: featurePayload.currentPlan || { name: currentPlanName },
            recommendedPlan:
              extractRecommendedPlan(featurePayload, "ADVANCED_REPORTS") ||
              extractRecommendedPlan(featurePayload, "EXECUTIVE_DASHBOARD") ||
              extractRecommendedPlan(featurePayload, "BILLING_ADVANCED") ||
              null
          })
          setError("")
          resetBillingData()
          return
        }

        throw new Error(
          result?.error?.message || "No se pudo cargar cartera y cobros."
        )
      }

      const data = result.data || null

      setBillingFeatureLock(null)
      setSummary(data?.summary || null)
      setTopDebtors(Array.isArray(data?.topDebtors) ? data.topDebtors : [])
      setAging(
        data?.aging
          ? {
              buckets: Array.isArray(data.aging?.buckets) ? data.aging.buckets : [],
              rows: Array.isArray(data.aging?.rows) ? data.aging.rows : []
            }
          : EMPTY_AGING
      )
      setCreditBalance(
        data?.creditBalance
          ? {
              totalCreditBalance: Number(data.creditBalance?.totalCreditBalance || 0),
              patientsWithCredit: Number(data.creditBalance?.patientsWithCredit || 0),
              rows: Array.isArray(data.creditBalance?.rows) ? data.creditBalance.rows : []
            }
          : EMPTY_CREDIT_BALANCE
      )
    } catch (err) {
      setBillingFeatureLock(null)
      resetBillingData()
      setError(err?.message || "No se pudo cargar cartera y cobros.")
    } finally {
      setLoading(false)
    }
  }, [
    clinicId,
    dateFrom,
    dateTo,
    plan,
    canBillingAdvancedByPlan,
    currentPlanName,
    isAllClinicsContext,
    resetBillingData
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  const openPatientFinance = (patientId, focus = "RECEIVABLES") => {
    if (!patientId) return

    const params = new URLSearchParams()
    params.set("tab", "FINANCE")
    params.set("focus", focus)
    if (clinicId) params.set("clinicId", clinicId)

    navigate(`/patients/${patientId}?${params.toString()}`)
  }

  const openPatientsPage = () => {
    const params = new URLSearchParams()
    if (clinicId) params.set("clinicId", clinicId)
    navigate(`/patients?${params.toString()}`)
  }

  const totalReceivables = Number(summary?.receivablesPending || 0)
  const totalCollected = Number(summary?.collected || 0)
  const totalCredits = Number(creditBalance?.totalCreditBalance || 0)
  const agingTotal = (aging?.buckets || []).reduce(
    (sum, bucket) => sum + Number(bucket.total || 0),
    0
  )

  const debtorsQueue = useMemo(() => {
    return topDebtors.map((item, index) => {
      const totalPending = Number(item.totalPending || 0)
      return {
        type: "DEBTOR",
        patientId: item.patientId,
        patientName: item.patientName || "Paciente",
        amount: totalPending,
        receivablesCount: Number(item.receivablesCount || 0),
        oldestDebtDate: item.oldestDebtDate || null,
        oldestDebtDays: Number(item.oldestDebtDays || 0),
        priority: getDebtPriority(totalPending),
        isTop: index === 0
      }
    })
  }, [topDebtors])

  const creditsQueue = useMemo(() => {
    return (creditBalance?.rows || []).map((item) => {
      const totalCredit = Number(item.totalCredit || 0)
      return {
        type: "CREDIT",
        patientId: item.patientId,
        patientName: item.patientName || "Paciente",
        amount: totalCredit,
        priority: getCreditPriority(totalCredit)
      }
    })
  }, [creditBalance])

  const agingQueue = useMemo(() => {
    return (aging?.rows || []).map((item, index) => {
      const bucketLabel = getBucketLabel(item.bucket, index)

      return {
        type: "AGING",
        patientId: item.patientId,
        patientName: item.patientName || "Paciente",
        amount: Number(item.balance || 0),
        concept: item.concept || "Cuenta por cobrar",
        createdAt: item.createdAt || null,
        ageDays: Number(item.ageDays || 0),
        bucket: bucketLabel,
        priority: getBucketPriority(bucketLabel)
      }
    })
  }, [aging])

  const matchesSearch = useCallback(
    (row) => {
      const q = normalizeText(search)
      if (!q) return true

      const haystack = [
        row.patientName,
        row.concept,
        row.bucket,
        row.priority
      ]
        .filter(Boolean)
        .map((item) => normalizeText(item))
        .join(" ")

      return haystack.includes(q)
    },
    [search]
  )

  const filteredDebtors = useMemo(
    () => debtorsQueue.filter(matchesSearch),
    [debtorsQueue, matchesSearch]
  )

  const filteredCredits = useMemo(
    () => creditsQueue.filter(matchesSearch),
    [creditsQueue, matchesSearch]
  )

  const filteredAging = useMemo(
    () => agingQueue.filter(matchesSearch),
    [agingQueue, matchesSearch]
  )

  const exportCurrentTabCsv = () => {
    if (!effectiveCanBillingAdvanced) {
      openUpgrade("billingAdvanced")
      return
    }

    if (workTab === "DEBTORS") {
      downloadCsv(
        `cobros_deudores_${dateFrom}_${dateTo}.csv`,
        ["Paciente", "Pendiente", "CxC", "Días", "Deuda más antigua", "Prioridad"],
        filteredDebtors.map((item) => [
          item.patientName,
          item.amount,
          item.receivablesCount,
          item.oldestDebtDays,
          formatDateOnly(item.oldestDebtDate),
          item.priority
        ])
      )
      return
    }

    if (workTab === "CREDITS") {
      downloadCsv(
        `cobros_saldo_a_favor_${dateFrom}_${dateTo}.csv`,
        ["Paciente", "Saldo a favor", "Prioridad"],
        filteredCredits.map((item) => [
          item.patientName,
          item.amount,
          item.priority
        ])
      )
      return
    }

    downloadCsv(
      `cobros_antiguedad_${dateFrom}_${dateTo}.csv`,
      ["Paciente", "Concepto", "Saldo", "Días", "Tramo", "Prioridad"],
      filteredAging.map((item) => [
        item.patientName,
        item.concept,
        item.amount,
        item.ageDays,
        item.bucket,
        item.priority
      ])
    )
  }

  const pageStyle = {
    padding: isMobile ? 10 : 20,
    background: "#f8fafc",
    minHeight: "100vh",
    display: "grid",
    gap: isMobile ? 10 : 16,
    overflowX: "hidden"
  }

  const heroGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "minmax(220px, 260px) minmax(220px, 260px) repeat(auto-fit, minmax(180px, 1fr))",
    gap: isMobile ? 10 : 12
  }

  const workToolbarStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto",
    gap: isMobile ? 10 : 12,
    alignItems: "end"
  }

  return (
    <div style={pageStyle}>
      <HeroCard
        title="Cobros"
        subtitle="Mesa de trabajo para cobrar, aplicar saldo a favor y seguir cartera sin mezclarla con la lectura gerencial."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={openPatientsPage}
              style={{
                ...buttonStyle,
                background: "#ffffff"
              }}
            >
              Abrir pacientes
            </button>

            <button
              onClick={exportCurrentTabCsv}
              disabled={!effectiveCanBillingAdvanced}
              style={{
                ...buttonStyle,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                opacity: effectiveCanBillingAdvanced ? 1 : 0.7,
                cursor: effectiveCanBillingAdvanced ? "pointer" : "not-allowed"
              }}
            >
              Exportar vista
            </button>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              ...heroGridStyle
            }}
          >
            <div>
              <FieldLabel>Desde</FieldLabel>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <FieldLabel>Hasta</FieldLabel>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={inputStyle}
              />
            </div>

            <HeroMetric label="Cartera pendiente" value={formatMoney(totalReceivables)} tone="warning" />
            <HeroMetric label="Cobrado" value={formatMoney(totalCollected)} />
            <HeroMetric label="Saldo a favor" value={formatMoney(totalCredits)} />
            <HeroMetric label="Antigüedad total" value={formatMoney(agingTotal)} />
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 16,
              padding: 14
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                color: "#64748b",
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: 0.35
              }}
            >
              Acción sugerida
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 18,
                fontWeight: 900,
                color: "#0f172a",
                letterSpacing: "-0.02em"
              }}
            >
              {Number(totalReceivables || 0) > 0
                ? "Atacar cartera pendiente"
                : Number(totalCredits || 0) > 0
                  ? "Aplicar saldos a favor"
                  : "Sin urgencia operativa visible"}
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: "#64748b",
                lineHeight: 1.45
              }}
            >
              Usa esta página para trabajar cobranza del periodo; el análisis más gerencial se queda en Finanzas ejecutivas.
            </div>
          </div>
        </div>
      </HeroCard>

      {isReadOnly ? (
        <StateCard tone="danger" title="Cobros en solo lectura">
          {readOnlyReason}
        </StateCard>
      ) : null}

      {!clinicId ? (
        <StateCard tone="danger" title={isAllClinicsContext ? "Selecciona una clínica" : "Sin clínica activa"}>
          {isAllClinicsContext
            ? "Cobros es una mesa operativa por clínica. Selecciona una clínica específica para trabajar cartera y pagos."
            : "No hay una clínica válida seleccionada para trabajar cobros."}
        </StateCard>
      ) : null}

      <SectionCard
        title="Acciones rápidas"
        subtitle="Atajos directos al trabajo de cobro y aplicación."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12
          }}
        >
          <ActionCard
            title="Cobrar cuenta por cobrar"
            helper="Abre el estado financiero del paciente para registrar pago."
            buttonLabel="Ir a pacientes"
            onClick={openPatientsPage}
          />
          <ActionCard
            title="Aplicar saldo a favor"
            helper="Entra al paciente con saldo para aplicarlo manualmente."
            buttonLabel="Ver saldos"
            onClick={() => setWorkTab("CREDITS")}
          />
          <ActionCard
            title="Revisar antigüedad"
            helper="Ubica lo más vencido y decide seguimiento."
            buttonLabel="Ver antigüedad"
            onClick={() => setWorkTab("AGING")}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Mesa de trabajo"
        subtitle="Lista operativa según el tipo de seguimiento que quieras hacer."
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              ...workToolbarStyle
            }}
          >
            <div>
              <FieldLabel>Buscar paciente, concepto o tramo</FieldLabel>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ej. Juan, ortodoncia, tramo 61-90..."
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <TabChip
                active={workTab === "DEBTORS"}
                label="Por cobrar"
                onClick={() => setWorkTab("DEBTORS")}
              />
              <TabChip
                active={workTab === "CREDITS"}
                label="Saldo a favor"
                onClick={() => setWorkTab("CREDITS")}
              />
              <TabChip
                active={workTab === "AGING"}
                label="Antigüedad"
                onClick={() => setWorkTab("AGING")}
              />
            </div>
          </div>

          {!effectiveCanBillingAdvanced ? (
            <div style={{ marginTop: 4 }}>
              <SubscriptionFeatureAccess
                enabled={false}
                featureKey="billingAdvanced"
                planName={currentPlanName}
                subtitle="Desbloquea la vista completa de cobranza, antigüedad ampliada, saldo a favor y exportación."
                onUpgrade={() => openUpgrade("billingAdvanced")}
                minHeight={280}
              />
            </div>
          ) : loading ? (
            <StateLine text="Cargando cartera y cobros..." />
          ) : error ? (
            <StateCard tone="danger" title="No se pudo cargar Cobros">
              {error}
            </StateCard>
          ) : (
            <>
              {workTab === "DEBTORS" ? (
                <SimpleTable
                  mobile={isMobile}
                  headers={["Paciente", "Pendiente", "CxC", "Días", "Prioridad", "Acciones"]}
                  rows={filteredDebtors.map((item) => [
                    <div>
                      <div style={{ fontWeight: 900, color: "#0f172a" }}>{item.patientName}</div>
                      {item.isTop ? (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                          Deudor más alto del periodo
                        </div>
                      ) : null}
                    </div>,
                    formatMoney(item.amount),
                    String(item.receivablesCount),
                    String(item.oldestDebtDays),
                    <PriorityBadge priority={item.priority} />,
                    <ActionWrap>
                      <MiniActionButton
                        label="Cobrar"
                        tone="info"
                        onClick={() => openPatientFinance(item.patientId, "RECEIVABLES")}
                      />
                      <MiniActionButton
                        label="Pagos"
                        tone="neutral"
                        onClick={() => openPatientFinance(item.patientId, "PAYMENTS")}
                      />
                    </ActionWrap>
                  ])}
                  emptyLabel="Sin pacientes por cobrar en el rango."
                />
              ) : null}

              {workTab === "CREDITS" ? (
                <SimpleTable
                  mobile={isMobile}
                  headers={["Paciente", "Saldo a favor", "Prioridad", "Acciones"]}
                  rows={filteredCredits.map((item) => [
                    item.patientName,
                    formatMoney(item.amount),
                    <PriorityBadge priority={item.priority} />,
                    <ActionWrap>
                      <MiniActionButton
                        label="Aplicar saldo"
                        tone="info"
                        onClick={() => openPatientFinance(item.patientId, "MANUAL_APPLY")}
                      />
                      <MiniActionButton
                        label="Bitácora"
                        tone="neutral"
                        onClick={() => openPatientFinance(item.patientId, "TIMELINE")}
                      />
                    </ActionWrap>
                  ])}
                  emptyLabel="Sin saldos a favor en el rango."
                />
              ) : null}

              {workTab === "AGING" ? (
                <div style={{ display: "grid", gap: 14 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: 12
                    }}
                  >
                    {(aging?.buckets || []).map((bucket, index) => {
                      const bucketLabel = getBucketLabel(bucket?.bucket, index)

                      return (
                        <CompactMetric
                          key={`${bucketLabel}-${index}`}
                          label={`Tramo ${bucketLabel}`}
                          value={formatMoney(bucket.total || 0)}
                          tone={getBucketPriority(bucketLabel) === "CRITICAL"
                            ? "danger"
                            : getBucketPriority(bucketLabel) === "HIGH"
                              ? "warning"
                              : "neutral"}
                        />
                      )
                    })}
                  </div>

                  <SimpleTable
                    mobile={isMobile}
                    headers={["Paciente", "Concepto", "Saldo", "Días", "Tramo", "Acciones"]}
                    rows={filteredAging.map((item) => [
                      item.patientName,
                      item.concept,
                      formatMoney(item.amount),
                      String(item.ageDays),
                      <PriorityBadge priority={item.priority} />,
                      <ActionWrap>
                        <MiniActionButton
                          label="Ver CxC"
                          tone="info"
                          onClick={() => openPatientFinance(item.patientId, "RECEIVABLES")}
                        />
                      </ActionWrap>
                    ])}
                    emptyLabel="Sin antigüedad de cartera en el rango."
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  )
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box"
}

const buttonStyle = {
  minHeight: 40,
  padding: "9px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer",
  background: "#ffffff",
  color: "#0f172a",
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)"
}

function HeroCard({ title, subtitle, actions, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        display: "grid",
        gap: 16
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 14,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 0.45,
              textTransform: "uppercase",
              marginBottom: 10
            }}
          >
            Operación de cobranza
          </div>

          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-0.04em",
              lineHeight: 1.02
            }}
          >
            {title}
          </div>

          <div
            style={{
              color: "#64748b",
              marginTop: 6,
              fontSize: 13,
              lineHeight: 1.4,
              maxWidth: 760
            }}
          >
            {subtitle}
          </div>
        </div>

        {actions}
      </div>

      {children}
    </div>
  )
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.4
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function HeroMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 22,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
    </div>
  )
}

function CompactMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", value: "#991b1b" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 14,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 20,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ActionCard({ title, helper, buttonLabel, onClick }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 10
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{title}</div>
      <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.45 }}>{helper}</div>
      <button
        onClick={onClick}
        style={{
          ...buttonStyle,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1d4ed8"
        }}
      >
        {buttonLabel}
      </button>
    </div>
  )
}

function TabChip({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 36,
        padding: "8px 12px",
        borderRadius: 999,
        border: active ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
        background: active ? "#1d4ed8" : "#ffffff",
        color: active ? "#ffffff" : "#334155",
        cursor: "pointer",
        fontWeight: 900,
        fontSize: 12
      }}
    >
      {label}
    </button>
  )
}

function PriorityBadge({ priority }) {
  const map = {
    CRITICAL: {
      bg: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b",
      label: "Crítico"
    },
    HIGH: {
      bg: "#fffbeb",
      border: "#fde68a",
      color: "#92400e",
      label: "Alto"
    },
    MEDIUM: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
      label: "Medio"
    },
    LOW: {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
      label: "Bajo"
    }
  }

  const visual = map[priority] || map.LOW

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 30,
        padding: "4px 9px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 12,
        fontWeight: 900
      }}
    >
      {visual.label}
    </span>
  )
}

function ActionWrap({ children }) {
  return <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>
}

function MiniActionButton({ label, onClick, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#ffffff",
      border: "#cbd5e1",
      color: "#334155"
    },
    info: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8"
    }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 34,
        padding: "7px 10px",
        borderRadius: 10,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        cursor: "pointer",
        fontWeight: 800,
        fontSize: 12
      }}
    >
      {label}
    </button>
  )
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: 6,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800
      }}
    >
      {children}
    </label>
  )
}

function StateCard({ tone = "neutral", title, children }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", color: "#334155" },
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}

function StateLine({ text }) {
  return <div style={{ color: "#64748b" }}>{text}</div>
}

function SimpleTable({ headers, rows, emptyLabel, mobile = false }) {
  if (!rows.length) {
    return (
      <div
        style={{
          color: "#64748b",
          border: "1px dashed #e2e8f0",
          borderRadius: 14,
          padding: 14,
          background: "#f8fafc"
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  if (mobile) {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#ffffff",
              padding: 12,
              display: "grid",
              gap: 9,
              boxShadow: "0 6px 16px rgba(15,23,42,0.04)",
              minWidth: 0
            }}
          >
            {row.map((cell, cellIndex) => (
              <div
                key={cellIndex}
                style={{
                  display: "grid",
                  gap: 3,
                  minWidth: 0,
                  borderBottom: cellIndex === row.length - 1 ? "none" : "1px solid #f1f5f9",
                  paddingBottom: cellIndex === row.length - 1 ? 0 : 8
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    color: "#64748b",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: 0.35
                  }}
                >
                  {headers[cellIndex] || "Detalle"}
                </div>
                <div style={{ color: "#334155", fontSize: 13.5, minWidth: 0 }}>
                  {cell}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff"
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: "left",
                  fontSize: 11.5,
                  color: "#64748b",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "10px 8px",
                  whiteSpace: "nowrap",
                  background: "#f8fafc",
                  textTransform: "uppercase",
                  letterSpacing: 0.35
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, idx) => (
                <td
                  key={idx}
                  style={{
                    padding: "10px 8px",
                    borderBottom: "1px solid #f1f5f9",
                    fontSize: 14,
                    color: "#334155",
                    verticalAlign: "top",
                    whiteSpace: "nowrap"
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
